import React from 'react';
import {
  PROJECT_PL_BUCKET_HINTS,
  ProjectPLBucketRow,
} from '../../utils/projectPL';

interface BucketVarianceTableProps {
  buckets: ProjectPLBucketRow[];
  formatCurrency: (n: number) => string;
  onOpenActivity?: () => void;
  onOpenPos?: () => void;
}

/** Color band thresholds for variance % (over budget = positive, under budget = negative). */
function varianceClass(variancePct: number, budget: number, actual: number): string {
  // No budget yet — show neutral until proposal is in.
  if (budget <= 0 && actual <= 0) return 'sa-ppl-variance-neutral';
  if (budget <= 0) return 'sa-ppl-variance-warn';
  const abs = Math.abs(variancePct);
  if (abs <= 5) return 'sa-ppl-variance-ok';
  if (abs <= 15) return 'sa-ppl-variance-warn';
  return 'sa-ppl-variance-bad';
}

const ColLabel: React.FC<{
  children: React.ReactNode;
  badge: string;
  tone: 'proposal' | 'po' | 'qb';
  title?: string;
  onClick?: () => void;
}> = ({ children, badge, tone, title, onClick }) => (
  <span className="sa-ppl-col-label">
    <span className="sa-ppl-col-title">{children}</span>
    {onClick ? (
      <button
        type="button"
        className={`sa-ppl-src-badge sa-ppl-src-badge--${tone} sa-ppl-src-badge--btn`}
        onClick={onClick}
        title={title}
      >
        {badge}
      </button>
    ) : (
      <span className={`sa-ppl-src-badge sa-ppl-src-badge--${tone}`} title={title}>
        {badge}
      </span>
    )}
  </span>
);

const BucketVarianceTable: React.FC<BucketVarianceTableProps> = ({
  buckets,
  formatCurrency,
  onOpenActivity,
  onOpenPos,
}) => {
  const totals = buckets.reduce(
    (acc, b) => {
      acc.budget += b.budget;
      acc.committed += b.committed || 0;
      acc.actual += b.actual;
      return acc;
    },
    { budget: 0, committed: 0, actual: 0 },
  );
  const totalVariance = totals.actual - totals.budget;
  const totalVariancePct =
    totals.budget > 0 ? (totalVariance / totals.budget) * 100 : totals.actual > 0 ? 100 : 0;

  return (
    <div className="sa-ppl-bucket-table-wrap">
      <table className="sa-ppl-bucket-table">
        <thead>
          <tr>
            <th>Bucket</th>
            <th className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--proposal">
              <ColLabel badge="Proposal" tone="proposal" title="Raw cost from the latest proposal">
                Budget
              </ColLabel>
            </th>
            <th className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--po">
              <ColLabel
                badge="POs"
                tone="po"
                title="Open purchase orders for this project"
                onClick={onOpenPos}
              >
                Committed
              </ColLabel>
            </th>
            <th className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--qb">
              <ColLabel
                badge="QB"
                tone="qb"
                title="QuickBooks actuals. Click to view activity."
                onClick={onOpenActivity}
              >
                Actual
              </ColLabel>
            </th>
            <th className="sa-ppl-num">Variance</th>
            <th className="sa-ppl-num">%</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map(b => {
            const cls = varianceClass(b.variancePct, b.budget, b.actual);
            const showRow = b.budget > 0 || b.actual > 0 || (b.committed || 0) > 0;
            if (!showRow) return null;
            const committedTitle = (b.committedLines || [])
              .map(line => `${line.label} ${formatCurrency(line.amount)}`)
              .join('\n');
            return (
              <tr key={b.bucket}>
                <td>
                  <span className="sa-ppl-bucket-label" title={PROJECT_PL_BUCKET_HINTS[b.bucket]}>
                    {b.label}
                  </span>
                </td>
                <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--proposal">{formatCurrency(b.budget)}</td>
                <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--po" title={committedTitle || undefined}>
                  {formatCurrency(b.committed || 0)}
                </td>
                <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--qb">{formatCurrency(b.actual)}</td>
                <td className={`sa-ppl-num ${cls}`}>
                  {b.variance >= 0 ? '+' : ''}
                  {formatCurrency(b.variance)}
                </td>
                <td className={`sa-ppl-num ${cls}`}>
                  {b.budget > 0 ? `${b.variance >= 0 ? '+' : ''}${b.variancePct.toFixed(0)}%` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Totals</td>
            <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--proposal">{formatCurrency(totals.budget)}</td>
            <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--po">{formatCurrency(totals.committed)}</td>
            <td className="sa-ppl-num sa-ppl-num-badge sa-ppl-num-badge--qb">{formatCurrency(totals.actual)}</td>
            <td className={`sa-ppl-num ${varianceClass(totalVariancePct, totals.budget, totals.actual)}`}>
              {totalVariance >= 0 ? '+' : ''}
              {formatCurrency(totalVariance)}
            </td>
            <td className={`sa-ppl-num ${varianceClass(totalVariancePct, totals.budget, totals.actual)}`}>
              {totals.budget > 0 ? `${totalVariance >= 0 ? '+' : ''}${totalVariancePct.toFixed(0)}%` : '—'}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default BucketVarianceTable;
